import { useTranslation } from "react-i18next";
import { useSplitShipmentMutation } from "../../../graphql/generated";
import useDialog from "../../../utils/hooks/useDialog";
import LoadSplittingModal, {
  LoadSplittingModalProps,
} from "./LoadSplittingModal";

export type SplitShipmentCompleteResult = {
  parentId: string;
  childIds: string[];
};

type LoadSplittingModalContainerProps = Omit<
  LoadSplittingModalProps,
  "onSubmit" | "splitting"
> & {
  onSplitComplete?: (
    result: SplitShipmentCompleteResult
  ) => void | Promise<void>;
};

const LoadSplittingModalContainer = ({
  onSplitComplete,
  ...props
}: LoadSplittingModalContainerProps) => {
  const { t } = useTranslation(["orders", "common"]);
  const splitShipmentMutation = useSplitShipmentMutation();
  const { showDialog } = useDialog();
  return (
    <LoadSplittingModal
      {...props}
      splitting={splitShipmentMutation.isLoading}
      onSubmit={async (shipmentId, locations) => {
        try {
          if (!locations.every((location) => !!location.addressLabel)) {
            throw new Error(
              t(
                "invalidLocationInput",
                "Invalid Location: Please select a correct location."
              )
            );
          }
          const result = await splitShipmentMutation.mutateAsync({
            shipmentId,
            locations,
          });
          const childIds = (result.splitShipment || []).map(
            (shipment) => shipment._id
          );
          await onSplitComplete?.({
            parentId: shipmentId,
            childIds,
          });
          await props.onClose();
        } catch (error) {
          showDialog({
            title: t("common:error.title", "Error"),
            description:
              t(
                "error.splitError",
                `An error occurred while splitting the load. `
              ) + (error as Error)?.message ||
              t("common:error.unknownError", "Unknown error"),
            type: "error",
          });
        }
      }}
    />
  );
};

export default LoadSplittingModalContainer;
